import React, { PropsWithChildren } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Computer, Instagram } from '@mui/icons-material';
import { toast } from 'react-toastify';
import PageLayout from '../components/global/PageLayout';
import useUser from '../hooks/global/useUser';
import { FooterAction, LoadedUser, Row } from '../interfaces';
import { isUserAdminOrManager } from '../utils/global/helper';
import ReuseableTable from '../components/table/ReuseableTable';
import DropdownMenu from '../components/global/DropdownMenu';
import {
  filterBookableServices,
  getBusinessArtistUrl,
  getBusinessShopUrl,
} from '../helpers/businesses';
import FullPageModal from '../components/modal/FullPageModal';
import StepsFlow, { StepFormData } from '../components/global/StepsFlow';
import { CreateBookingLinkPayload } from '../types/businesses';
import StepFlowImage from '../components/global/StepsFlow/StepFlowImage';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import useCompleteOnboardingStep from '../hooks/businesses/useCompleteOnboardingStep';

const BOOKING_LINK_IMG =
  'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fassets%2Fig-profile-booking-link.webp?alt=media';
const WEB_SHOP_BOOKING_LINK_IMG =
  'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fassets%2Fweb-shop-booking-link.webp?alt=media';
const WEB_ARTIST_BOOKING_LINK_IMG =
  'https://firebasestorage.googleapis.com/v0/b/bamboo-artwork-372213.appspot.com/o/public%2Fassets%2Fweb-artist-booking-link.webp?alt=media';

function Bold({ children }: PropsWithChildren) {
  return (
    <Typography
      variant="inherit"
      fontWeight={700}
      display="inline"
      component="span">
      {children}
    </Typography>
  );
}

const WEB_ARTIST_INSTRUCTIONS = [
  'Send the link above to your website developer, or open your website editor.',
  `This link is for {{ARTIST}}. Open the artist's profile in your website editor and replace any “Book with {{ARTIST}}" buttons with the link you copied above`,
  'Publish your website with the new link changes',
];
const WEB_SHOP_INSTRUCTIONS = [
  'Send the link above to your website developer, or open your website editor.',
  "This is your shop booking link, so replace any Book Now buttons that aren't specific to an artist with the link you copied",
  'Publish your website with the new link changes',
];
const WEB_SERVICE_INSTRUCTIONS = [
  'Send the link above to your website developer, or open your website editor.',
  `This link is for {{SERVICE}}. Open the service booking page in your website editor and replace any “Book {{SERVICE}}" buttons with the link you copied above`,
  'Publish your website with the new link changes',
];
const IG_INSTRUCTIONS = [
  <Typography variant="body2">
    Open your <Bold>Instagram Profile</Bold>
  </Typography>,
  <Typography variant="body2">
    Tap <Bold>Edit Profile</Bold>
  </Typography>,
  <Typography variant="body2">
    Tap on <Bold>Links</Bold> and <Bold>Add external link</Bold>.
  </Typography>,
  <Typography variant="body2">
    Copy and paste the link above into the <Bold>URL</Bold>
  </Typography>,
  <Typography variant="body2">
    Add a message to your <Bold>Bio</Bold>, &quot;Book with me using the link
    below!&quot;
  </Typography>,
];

const getRow = (
  name: string,
  link: string,
  actions: { onCopy: () => void | Promise<void>; create: () => void },
): Row => {
  const options: FooterAction[] = [
    {
      label: 'Copy link',
      action: async () => {
        try {
          navigator.clipboard.writeText(link);
          actions.onCopy?.();
          toast.success('Link copied to clipboard!', {
            autoClose: 1500,
          });
        } catch {
          // Do nothing
        }
      },
    },
    {
      label: 'Post link',
      action: actions.create,
    },
  ];

  // capital or not - doesnt matter

  return {
    cells: [
      <Typography variant="body2">{name}</Typography>,
      <Typography variant="body2">{link}</Typography>,
      <Box display="flex" justifyContent="end" alignItems="center">
        <DropdownMenu options={options} />
      </Box>,
    ],
    rowId: link,
  };
};

const getEntityName = (name: string | undefined) => {
  const endsInLetter = /[a-zA-Z]$/;
  const nameEndsInLetter = endsInLetter.test(name || '');

  return `${name || 'Unnamed'}${nameEndsInLetter ? "'s" : ''} Booking Link`;
};

export default function BookingLinksPage() {
  const [formData, setFormData] =
    React.useState<StepFormData<CreateBookingLinkPayload> | null>(null);

  const { user } = useUser<LoadedUser>();
  const { mutateAsync: completeOnboardingStep } = useCompleteOnboardingStep();

  const isStudio = user.business.type === 'STUDIO';
  const isIndependent = user.business.type === 'INDEPENDENT';

  const filteredEmployees =
    isIndependent || !isUserAdminOrManager(user)
      ? user.business.employees.filter(etb => etb.employeeId === user.userId)
      : user.business.employees;

  const bookableServices = filterBookableServices(user.business.services);

  const onCompleteOnboardingStep = async () => {
    await completeOnboardingStep('BOOKING_LINK');
  };

  const onCreateLink = (id?: string) => {
    const options = filteredEmployees.map(etb => ({
      value: etb.employeeId,
      label: getEntityName(etb.displayName),
    }));

    if (isStudio) {
      options.unshift({
        value: user.business.id,
        label: getEntityName(user.business.name),
      });

      if (bookableServices?.length) {
        options.push(
          ...bookableServices.map(service => ({
            value: service.serviceId.toString(),
            label: service.serviceName,
          })),
        );
      }
    }

    const entityId =
      id || (options.length === 1 ? (options[0]?.value as string) : '');

    const form: StepFormData<CreateBookingLinkPayload> = {
      initialValues: {
        entityId: entityId || '',
        destination: 'instagram',
        placeholder: true, // last step doesn't have a value
      },
      onSubmit: async () => {
        await onCompleteOnboardingStep();
        setFormData(null);
      },
      submitText: "I've posted my link!",
      steps: [
        {
          title: 'Choose your link',
          description: 'Which link do you want to post?',
          isSubmit: false,
          inputs: [
            {
              name: 'entityId',
              contentType: 'select',
              options,
              autoWidth: false,
              placeholder: 'Select Link',
            },
          ],
        },
        {
          title: 'Choose the destination',
          description: 'Where do you want to post the link?',
          inputs: [
            {
              name: 'destination',
              contentType: 'button-select',
              options: [
                {
                  value: 'instagram',
                  title: 'Instagram',
                  icon: Instagram,
                  description: 'Add booking link to your instagram profile',
                },
                {
                  value: 'web',
                  title: 'Website',
                  icon: Computer,
                  description: 'Add booking links to your shop website',
                },
              ],
            },
          ],
        },
        {
          title: 'Post your link',
          description: 'Let clients book from anywhere!',
          isSubmit: true,
          inputs: [
            {
              name: 'placeholder',
              contentType: 'copy',
              getCopyText: values => {
                const isBusiness = values.entityId === user.business.id;
                const employee = user.business.employees.find(
                  etb => etb.employeeId === values.entityId,
                );
                const service = bookableServices?.find(
                  s => s.serviceId.toString() === values.entityId,
                );

                if (!isBusiness && !employee && !service) return '';
                const link = isBusiness
                  ? getBusinessShopUrl(user.business)
                  : employee
                  ? getBusinessArtistUrl(employee)
                  : getBusinessShopUrl(
                      user.business,
                      `service/${service?.serviceId}`,
                    );
                return link;
              },
              getInstructions: values => {
                const isWeb = values.destination === 'web';
                const isBusiness = values.entityId === user.business.id;
                const isEmployee = !!user.business.employees.find(
                  etb => etb.employeeId === values.entityId,
                );

                return isWeb
                  ? isBusiness
                    ? WEB_SHOP_INSTRUCTIONS
                    : isEmployee
                    ? WEB_ARTIST_INSTRUCTIONS.map(instruction =>
                        instruction.replaceAll(
                          '{{ARTIST}}',
                          user.business.employees.find(
                            etb => etb.employeeId === values.entityId,
                          )?.displayName || 'Unnamed Artist',
                        ),
                      )
                    : WEB_SERVICE_INSTRUCTIONS.map(instruction =>
                        instruction.replaceAll(
                          '{{SERVICE}}',
                          bookableServices?.find(
                            s => s.serviceId.toString() === values.entityId,
                          )?.serviceName || 'Unnamed Service',
                        ),
                      )
                  : IG_INSTRUCTIONS;
              },
              onCopy: onCompleteOnboardingStep,
            },
          ],
        },
      ],
    };
    setFormData(form);
  };

  const shopLink = isStudio
    ? getRow(
        getEntityName(user?.business?.name),
        getBusinessShopUrl(user?.business),
        {
          onCopy: onCompleteOnboardingStep,
          create: () => onCreateLink(user?.business?.id),
        },
      )
    : null;
  let rows = [
    shopLink,
    ...filteredEmployees.map(etb =>
      getRow(getEntityName(etb.displayName), getBusinessArtistUrl(etb), {
        onCopy: onCompleteOnboardingStep,
        create: () => onCreateLink(etb.employeeId),
      }),
    ),
  ].filter(Boolean) as Row[];

  if (bookableServices?.length) {
    const servicesRows = bookableServices
      ?.map(service =>
        getRow(
          service.serviceName,
          getBusinessShopUrl(user.business, `service/${service.serviceId}`),
          {
            onCopy: onCompleteOnboardingStep,
            create: () => onCreateLink(service.serviceId.toString()),
          },
        ),
      )
      .filter(Boolean) as Row[];

    rows = [...rows, ...servicesRows];
  }

  const rightContent = (values: CreateBookingLinkPayload) => {
    const isBusiness = values.entityId === user.business.id;
    const isWeb = values.destination === 'web';
    const src = isWeb
      ? isBusiness
        ? WEB_SHOP_BOOKING_LINK_IMG
        : WEB_ARTIST_BOOKING_LINK_IMG
      : BOOKING_LINK_IMG;

    return (
      <StepFlowImage
        src={src}
        isFullWidth={isWeb}
        gradient={isWeb}
        isOffsetRight={isWeb}
        keepPrevious
      />
    );
  };

  return (
    <>
      <PageLayout
        title="Booking Links"
        guard={{
          isAllowed:
            isUserAdminOrManager(user) || user.business.type === 'INDEPENDENT',
        }}
        topSection={<OnboardingBanner step="BOOKING_LINK" />}>
        <div className="header-right">
          <Button
            variant="contained"
            className="primary"
            onClick={() => onCreateLink()}>
            Create & Post A Link
          </Button>
        </div>
        <ReuseableTable
          name="All Booking Links"
          emptyMessage="No booking links added yet!"
          headerCells={[
            { id: 'name', label: 'Name' },
            { id: 'link', label: 'Link' },
            { id: 'actions', label: '' },
          ]}
          loading={false}
          rows={rows}
        />
      </PageLayout>
      <FullPageModal
        name="Create & Post A Link"
        large
        modalOpen={!!formData}
        setModalOpen={() => setFormData(null)}>
        {!!formData && (
          <StepsFlow isSequential form={formData} rightContent={rightContent} />
        )}
      </FullPageModal>
    </>
  );
}
